// FREKVENCIA (rekurencia) — JEDEN zdieľaný ovládač pre návyk aj výzvu.
//
// Do feat/vyzva-frekvencia mal návyk plnú H3 rekurenciu (Každý deň + „viackrát za deň",
// N× za týždeň/mesiac/pol rok/rok, vlastné dni v týždni), kým výzva len chudobnú dvojicu
// „denne / N× do týždňa / vlastné dni". Formulár výzvy si vtedy ovládač kopíroval — tento
// súbor tú kópiu ruší: `AddCard` aj `ChallengeCreate` renderujú TENTO komponent, takže obe
// obrazovky majú rovnaké možnosti aj rovnaký vzhľad, a ďalšia zmena stačí na jednom mieste.
//
// Hodnota je jeden objekt {mode, n, days, period, perDay} — 1:1 s rekurenčnými stĺpcami
// `tasks`/`challenges` (recur_mode, recur_n, recur_days, recur_period, per_day).
import { Field, Select, Segmented } from "../ui/ds";
import { st } from "../../styles.js";
import { RECUR_PERIODS, RECUR_PERIOD_MAX } from "../../lib/constants";
import { isMasCas } from "../../lib/recur";
import { WEEKDAYS_SK } from "../../lib/format";

export const DEFAULT_RECUR = { mode: "daily", n: 2, days: [], period: "week", perDay: 1 };

/** Riadok z API (návyk alebo výzva) → hodnota tohto ovládača. */
export function recurFromRow(src) {
  if (!src) return { ...DEFAULT_RECUR };
  return {
    mode: src.recur_mode || "daily",
    n: src.recur_n || DEFAULT_RECUR.n,
    days: src.recur_days ? String(src.recur_days).split(",").map(Number).filter(Boolean) : [],
    period: src.recur_period || "week",
    perDay: src.per_day || 1,
  };
}

/** Hodnota ovládača → telo požiadavky (rovnaké kľúče pre `/api/tasks` aj `/api/challenges`). */
export function recurToBody(v) {
  return {
    recur_mode: v.mode, recur_n: v.n, recur_days: v.days, recur_period: v.period,
    per_day: v.mode === "daily" ? (Number(v.perDay) || 1) : 1,
  };
}

// `noPerDay` — merateľný návyk („vlastný výsledok"): „koľkokrát za deň" tam nedáva zmysel,
// opakovanie počas dňa je už v nazbieranej hodnote (3 poháre = 3 zápisy). Krok sa preto skryje,
// nech formulár nesľubuje nastavenie, ktoré sa aj tak neuloží.
export function RecurrenceField({ value, onChange, label = "Frekvencia", hint = null, noPerDay = false,
  rareHint = "Zriedkavý návyk — na Dnes bude v sekcii „Máš čas\" pod bežnými návykmi." }) {
  const v = { ...DEFAULT_RECUR, ...(value || {}) };
  const set = (patch) => onChange && onChange({ ...v, ...patch });
  // segment: Každý deň / Za obdobie / Vlastné dni. 'interval' (každý N. deň) sa v UI netvorí,
  // ale keď ho návyk má, spadne pod „Vlastné" — presne ako doteraz.
  const seg = v.mode === "daily" ? "day"
    : (v.mode === "weekly_count" || v.mode === "period") ? "obdobie" : "custom";
  // aktuálne obdobie: weekly_count ≡ týždeň, period = zvolené obdobie
  const curPeriod = v.mode === "weekly_count" ? "week" : v.mode === "period" ? v.period : "week";
  const periodMax = RECUR_PERIOD_MAX[curPeriod] || 6;
  // týždeň sa ukladá ako weekly_count (jeden kód-path na backende), ostatné ako 'period'
  const setPeriod = (p) => {
    const max = RECUR_PERIOD_MAX[p] || 6;
    set(p === "week"
      ? { period: p, mode: "weekly_count", n: Math.max(1, Math.min(6, v.n || 1)) }
      : { period: p, mode: "period", n: Math.max(1, Math.min(max, v.n || 1)) });
  };
  const mascas = v.mode === "period" && isMasCas(v.period, v.n);
  const onSeg = (val) => {
    if (val === "day") set({ mode: "daily" });
    else if (val === "obdobie") { if (v.mode !== "weekly_count" && v.mode !== "period") setPeriod(v.period || "week"); }
    else if (v.mode !== "weekdays") set({ mode: "weekdays", n: 1 });
  };
  const toggleDay = (dn) => set({
    mode: "weekdays", n: 1,
    days: v.days.includes(dn) ? v.days.filter((x) => x !== dn) : [...v.days, dn],
  });
  return (
    <Field label={label}>
      <div className="bz-recur">
        <Segmented className="bz-seg--block bz-seg--recur" value={seg} onChange={onSeg}
          options={[{ value: "day", label: "Každý deň" }, { value: "obdobie", label: "Za obdobie" }, { value: "custom", label: "Vlastné" }]} />
        <div className="bz-recur__body">
          {seg === "day" ? (noPerDay ? null : (
            <label style={{ ...st.inlineLabel, margin: 0 }}><span>Koľkokrát za deň</span>
              <span className="bz-recur__stepper">
                <button type="button" aria-label="menej" onClick={() => set({ perDay: Math.max(1, (Number(v.perDay) || 1) - 1) })} style={st.iconBtn}>−</button>
                <strong className="bz-recur__count">{v.perDay}×</strong>
                <button type="button" aria-label="viac" onClick={() => set({ perDay: Math.min(12, (Number(v.perDay) || 1) + 1) })} style={st.iconBtn}>+</button>
              </span></label>
          )) : seg === "obdobie" ? (
            <>
              <label style={{ ...st.inlineLabel, margin: 0 }}><span>Koľkokrát</span>
                <span className="bz-recur__stepper">
                  <button type="button" aria-label="menej" onClick={() => set({ n: Math.max(1, v.n - 1) })} style={st.iconBtn}>−</button>
                  <strong className="bz-recur__count">{v.n}×</strong>
                  <button type="button" aria-label="viac" onClick={() => set({ n: Math.min(periodMax, v.n + 1) })} style={st.iconBtn}>+</button>
                </span></label>
              <label style={{ ...st.inlineLabel, margin: 0 }}><span>Za</span>
                <Select value={curPeriod} onChange={(e) => setPeriod(e.target.value)} style={{ width: 150 }}>
                  {RECUR_PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </Select></label>
              {mascas && <div style={st.fieldHint}>{rareHint}</div>}
            </>
          ) : (
            <div className="bz-recur__days">
              {WEEKDAYS_SK.map((lbl, i) => {
                const dn = i + 1;
                return (
                  <button type="button" key={dn} onClick={() => toggleDay(dn)}
                    aria-pressed={v.days.includes(dn)}
                    className={`bz-recur__day${v.days.includes(dn) ? " bz-recur__day--on" : ""}`}>{lbl}</button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {hint}
    </Field>
  );
}
